/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 14:55:08
 *
 *******************************************************/
// @flow
//
import _ from 'lodash';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import {
  Card,
  SkeletonBodyText,
  ResourceList,
  ResourceItem,
  TextStyle,
  Button,
  Badge,
  Stack,
  Icon,
  Tooltip,
  ButtonGroup
} from '@shopify/polaris';
import { QuestionMarkMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { routes } from 'src/routes';
import history from 'src/routes/history';

import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import PopoverActionButton from 'src/components/views/theme-setup-v3/popover-action-button';
import { getThemeReviewUrl } from 'src/helpers';
import { getLiveThemeStatusV3Html, getLiveThemeStatusV3 } from 'src/helpers/theme-setup-helper';
import { getDateTime } from 'src/helpers/time-helper';
import empty from 'src/assets/images/filter-tree/empty-option.png';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as syncActions from 'actions/sync-actions';
import { site as siteConfig } from 'src/config';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import BannerUpgrade from './banner.upgrade';
import BannerUpgradeRunning from './banner.upgrade.running';
import BannerSuggestPublish from './banner.suggest.publish';
import IconRunning from 'src/assets/images/common/running.svg';
import { isThemeV3 } from 'src/helpers/theme-setup-helper';
import DidYouKnow from 'src/components/views/theme-setup-v3/did-you-know';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';

type Props = {
  getThemes: () => Promise<void>,
  trackEvent: (eventSource: string, eventName: string, label: string) => Promise<void>,
  getChecklist: () => Promise<void>,
  // Sync for Refresh button
  getFlowSyncThemeStatus: () => Promise<void>,
  startFlowSyncTheme: () => Promise<void>,
  resetSyncStatus: () => void,
  handleRemoveThemeV3: Function,

  getThemesLoading: boolean,
  themes: Array<Object>,
  flow: Object,
  syncProcessLoading: boolean
};

type ComposedProps = Props & WithI18nProps;
type State = {
  themeUpgrading: Object
};

class Themes extends Component<ComposedProps, State> {
  timeoutGetSyncProcessStatus = undefined;
  state = {
    themeUpgrading: null
  };

  componentDidMount() {
    const { getThemes } = this.props;
    getThemes();
  }

  componentDidUpdate(prevProps) {
    const { flow: prevFlow, syncProcessLoading: prevSyncProcessLoading } = prevProps;
    const {
      flow: currentFlow,
      syncProcessLoading: currentSyncProcessLoading,
      getThemes,
      getFlowSyncThemeStatus,
      i18n
    } = this.props;
    const { status: prevFlowStatus } = prevFlow;
    const { status: currentFlowStatus } = currentFlow;

    if (prevSyncProcessLoading && !currentSyncProcessLoading) {
      // If first getFlowSyncThemeStatus() fails -> flowStatus will be unchanged
      if (currentFlowStatus !== 'running' && prevFlowStatus === currentFlowStatus) {
        // Call getFlowSyncThemeStatus() again

        setTimeout(() => {
          getFlowSyncThemeStatus();
        }, 2000);

        return;
      }

      switch (currentFlowStatus) {
        case 'done':
          getThemes();
          break;
        case 'error':
        case 'locked':
        case 'pending':
          showSABToast(i18n.translate('AdminHTML.ThemeSetup.Themes.errorMessage'), true);
          break;
        default:
          this.timeoutGetSyncProcessStatus = setTimeout(() => {
            getFlowSyncThemeStatus();
          }, siteConfig.syncStatusTimeout);
          break;
      }
    }
  }

  componentWillUnmount() {
    const { resetSyncStatus } = this.props;
    resetSyncStatus();

    clearTimeout(this.timeoutGetSyncProcessStatus);
  }

  handleRefresh = async () => {
    const { startFlowSyncTheme, getFlowSyncThemeStatus, i18n } = this.props;
    await startFlowSyncTheme();
    await getFlowSyncThemeStatus();
    showSABToast(i18n.translate('AdminHTML.ThemeSetup.Themes.infoMessage'), false);
  };

  handleUpgrade = theme => {
    const { handleUpgrade } = this.props;
    handleUpgrade(theme);
  };

  render() {
    const {
      themes = [],
      getThemesLoading,
      flow,
      i18n,
      upgradeThemeLoading,
      storeExpired
    } = this.props;

    const { themeUpgrading } = this.state;
    const { status: flowStatus, runningProcess } = flow;
    const loading =
      getThemesLoading || (flowStatus === 'running' && runningProcess === 'SyncTheme');

    // Get the main theme by published status
    const mainTheme = themes.find(theme => theme.published) || themes[0] || {};

    // Get live theme status
    const status = getLiveThemeStatusV3(loading, mainTheme);

    const themesV3 = _.filter(themes, theme => isThemeV3(theme)).sort(
      (a, b) => b.updatedAt - a.updatedAt
    );
    const liveTheme = _.find(themes, theme => theme.published);
    const isLiveThemeLatestVersion = !!_.find(themes, theme => isThemeV3(theme) && theme.published);
    const getVersionText = theme => {
      const { themeLibV2 } = theme;
      const themeLibV3 = isThemeV3(theme);
      if (themeLibV3) return '3.0';
      if (themeLibV2) return '2.0';
      return '1.0';
    };

    let isShowBannerUngrade = false;
    let isShowBannerPublish = false;
    if (
      (themes.length > 0 && themesV3.length === 0) ||
      (!getThemesLoading && themes.length === 0)
    ) {
      isShowBannerUngrade = true;
    }
    if (themesV3.length > 0 && !isLiveThemeLatestVersion) {
      isShowBannerPublish = true;
    }

    return (
      <div>
        <div className="BC-Container__Notifications">
          {/* Installation reminder status banner */}
          <InstallationRemindStatusBanner />
          {isShowBannerUngrade && !storeExpired ? (
            <div style={{ marginBottom: '1.5rem' }}>
              <BannerUpgrade handleUpgrade={() => this.handleUpgrade(liveTheme)} />
            </div>
          ) : (
            <div style={{ marginBottom: '1.5rem' }}>
              <DidYouKnow />
            </div>
          )}
          {isShowBannerPublish && (
            <div style={{ marginBottom: '1.5rem' }}>
              <BannerSuggestPublish theme={themesV3[0]} />
            </div>
          )}
          {upgradeThemeLoading && themeUpgrading && (
            <div style={{ marginBottom: '1.5rem' }}>
              <BannerUpgradeRunning theme={themeUpgrading} />
            </div>
          )}
        </div>
        <Card>
          <Card.Section>
            {loading ? (
              <SkeletonBodyText lines={1} />
            ) : (
              <Stack>
                <Stack.Item fill>{getLiveThemeStatusV3Html(status, i18n)}</Stack.Item>
                <Stack.Item>
                  <div id="BC-Button__Theme-Setup-Refresh">
                    <Button plain disabled={loading} onClick={this.handleRefresh}>
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.ThemeSetup.Themes.button')}
                      </TextStyle>
                    </Button>
                    <Tooltip
                      content={i18n.translate('AdminHTML.ThemeSetup.Themes.tooltip')}
                      preferredPosition="above">
                      <Icon source={QuestionMarkMajor} color="base" />
                    </Tooltip>
                  </div>
                </Stack.Item>
              </Stack>
            )}
          </Card.Section>
          <Card.Section>
            {loading ? (
              <ResourceList
                resourceName={{ singular: 'theme', plural: 'themes' }}
                items={[...Array(3).keys()]}
                renderItem={item => {
                  return (
                    <ResourceItem id={item}>
                      <SkeletonBodyText lines={2} />
                    </ResourceItem>
                  );
                }}
              />
            ) : (
              <div className="Polaris-ResourceList__ResourceListWrapper">
                <ul className="Polaris-ResourceList" aria-live="polite">
                  {_.map(themes, item => {
                    const { id, name, updatedAt, published } = item;
                    return (
                      <li className="Polaris-ResourceItem__ListItem" key={id}>
                        <div className="Polaris-ResourceItem__ItemWrapper">
                          <div className="Polaris-ResourceItem">
                            <div className="Polaris-ResourceItem__Container">
                              <div className="Polaris-ResourceItem__Content">
                                <div className="theme-item">
                                  <div className="theme-item-left">
                                    <div>
                                      <TextStyle variation="strong">{name}</TextStyle>
                                      {published && (
                                        <span style={{ marginLeft: '5px' }}>
                                          <Badge status="success">
                                            {i18n.translate(
                                              'AdminHTML.ThemeSetup.ThemeSetup.badgeLive'
                                            )}
                                          </Badge>
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ marginTop: '0.2rem' }}>
                                      <span style={{ marginRight: '4px' }}>
                                        <TextStyle variation="subdued">
                                          {i18n.translate(
                                            'AdminHTML.ThemeSetup.NewThemeSetup.list_theme_info_version',
                                            {
                                              v: getVersionText(item)
                                            }
                                          )}
                                        </TextStyle>
                                      </span>
                                      {updatedAt && updatedAt !== 0 ? (
                                        <TextStyle variation="subdued">
                                          {i18n.translate(
                                            'AdminHTML.ThemeSetup.ThemeSetup.lastSaved',
                                            {
                                              nt_time: getDateTime(updatedAt)
                                            }
                                          )}
                                        </TextStyle>
                                      ) : (
                                        <TextStyle variation="subdued">
                                          {i18n.translate(
                                            'AdminHTML.ThemeSetup.ThemeSetup.notUpdated'
                                          )}
                                        </TextStyle>
                                      )}
                                    </div>
                                  </div>
                                  <div className="theme-item-right">
                                    <ButtonGroup>
                                      {upgradeThemeLoading &&
                                        themeUpgrading &&
                                        themeUpgrading.id === item.id && (
                                          <img
                                            className="running"
                                            src={IconRunning}
                                            alt="running"
                                          />
                                        )}

                                      <PopoverActionButton
                                        disabled={
                                          upgradeThemeLoading &&
                                          themeUpgrading &&
                                          themeUpgrading.id === item.id
                                        }
                                        theme={item}
                                        handleRemove={removeAll =>
                                          this.props.handleRemove(item, removeAll)
                                        }
                                        handleRemoveThemeV3={theme =>
                                          this.props.handleRemoveThemeV3(theme)
                                        }
                                        handleUpgrade={this.handleUpgrade}
                                        storeExpired={storeExpired}
                                      />

                                      <Button
                                        onClick={() => this.handlePreview(id)}
                                        disabled={
                                          upgradeThemeLoading &&
                                          themeUpgrading &&
                                          themeUpgrading.id === item.id
                                        }>
                                        {i18n.translate('AdminHTML.Common.Action.preview')}
                                      </Button>

                                      {isThemeV3(item) && (
                                        <Button
                                          onClick={() =>
                                            history.push(
                                              routes.themeSetting.editVisualDesign(item.id)
                                            )
                                          }>
                                          {i18n.translate(
                                            'AdminHTML.ThemeSetup.NewThemeSetup.editVisualDesign'
                                          )}
                                        </Button>
                                      )}
                                    </ButtonGroup>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {_.isEmpty(themes) && !loading ? (
              <div className="BC-EmptyTheme__ThemeSetup">
                <img src={empty} alt="Empty" />
                <p>{i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.noTheme')}</p>
              </div>
            ) : (
              ''
            )}
          </Card.Section>
        </Card>
      </div>
    );
  }

  // Handle preview filter action
  handlePreview = async (id: string) => {
    const { trackEvent, getChecklist, themes } = this.props;

    const previewUrl = getThemeReviewUrl(id);
    // Call to tracking event
    await trackEvent('themeSetup', 'preview', ' ');
    const previewTheme = themes.find(e => e?.id === id);
    mixpanelV3Helper.trackEventData('Preview theme', {
      feature: 'Theme Setup',
      selectedTheme: previewTheme?.name
    });

    // Track Full Story event
    trackFSEvent('Preview theme', {
      feature: 'Theme Setup',
      selectedTheme: previewTheme?.name
    });

    await getChecklist();

    window.open(previewUrl, '_blank');
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };
}

const mapStateToProps = (state, props) => {
  const { themeSetupV3, oauth } = state;
  const tenantApp = _.get(oauth, 'tenantApp', {});
  const statusStore = _.get(state.oauth, 'subscription.status', '');

  const storeExpired =
    statusStore === 'expired' ||
    statusStore === 'exceeded' ||
    statusStore === 'regularExpired' ||
    statusStore === 'oldSpecialDiscountExpired';

  return {
    storeExpired,
    getThemesLoading: _.get(themeSetupV3, 'getThemesLoading', true),
    themes: _.get(themeSetupV3, 'themes', []),
    flow: _.get(state.sync, 'flow', {}),
    syncProcessLoading: _.get(state.sync, 'syncProcessLoading', false),

    // This variable to check if upgrade theme to theme lib v3
    upgradeThemeLoading: _.get(themeSetupV3, 'upgradeThemeloading', false),
    upgradeThemeSuccess: _.get(themeSetupV3, 'upgradeThemeSuccess', false),
    upgradeThemeError: _.get(themeSetupV3, 'upgradeThemeError', undefined),
    upgradeTheme: _.get(themeSetupV3, 'upgradeTheme', {}),

    themeSetupV2: _.get(tenantApp, 'adminAppFeatures.themeSetupV2', false),
    defaultThemeSettings: false, //_.get(tenantApp, 'adminAppFeatures.defaultThemeSettings', false),
    settings: _.get(tenantApp, 'settings', {})
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getThemes: () => dispatch(themeSetupV3Action.getThemes(ownProps.i18n)),
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label)),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n)),
  // Sync for Refresh button
  getFlowSyncThemeStatus: () => dispatch(syncActions.getSyncProcessStatus('FlowSyncTheme')),
  startFlowSyncTheme: () => dispatch(syncActions.triggerSync('FlowSyncTheme', ownProps.i18n)),
  resetSyncStatus: () => dispatch(syncActions.resetSyncStatus())
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Themes));
